import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { TrendingUp, AlertTriangle, Info, ChevronDown, ChevronUp } from 'lucide-react';

const SimpleDCFCalculator = () => {
  const [fcf, setFcf] = useState('');
  const [growthRate, setGrowthRate] = useState('10');
  const [discountRate, setDiscountRate] = useState('10');
  const [terminalMultiple, setTerminalMultiple] = useState('15');
  const [showSensitivity, setShowSensitivity] = useState(false);

  const result = useMemo(() => {
    const fcfNum = parseFloat(fcf);
    const growth = parseFloat(growthRate) / 100;
    const discount = parseFloat(discountRate) / 100;
    const multiple = parseFloat(terminalMultiple);
    
    if (!fcfNum || fcfNum <= 0 || !growth || !discount || discount <= 0 || !multiple) {
      return null;
    }
    
    // Calculate 5-year DCF
    const years = 5;
    const cashFlows = [];
    let cumulativePV = 0;
    
    for (let year = 1; year <= years; year++) {
      const projectedFCF = fcfNum * Math.pow(1 + growth, year);
      const discountFactor = Math.pow(1 + discount, year);
      const presentValue = projectedFCF / discountFactor;
      
      cashFlows.push({
        year,
        projectedFCF,
        discountFactor,
        presentValue
      });
      
      cumulativePV += presentValue;
    }
    
    // Terminal value
    const yearFiveFCF = fcfNum * Math.pow(1 + growth, years);
    const terminalValue = yearFiveFCF * multiple;
    const terminalPV = terminalValue / Math.pow(1 + discount, years);
    
    const totalValue = cumulativePV + terminalPV;
    
    // Sensitivity analysis (±3% growth)
    const calculateValue = (g) => {
      let pv = 0;
      for (let year = 1; year <= years; year++) {
        const projectedFCF = fcfNum * Math.pow(1 + g, year);
        const presentValue = projectedFCF / Math.pow(1 + discount, year);
        pv += presentValue;
      }
      const y5fcf = fcfNum * Math.pow(1 + g, years);
      const tv = y5fcf * multiple;
      const tvPV = tv / Math.pow(1 + discount, years);
      return pv + tvPV;
    };
    
    const sensitivity = {
      low: calculateValue(growth - 0.03),
      base: totalValue,
      high: calculateValue(growth + 0.03)
    };
    
    return {
      fcf: fcfNum,
      growthRate: growth * 100,
      discountRate: discount * 100,
      terminalMultiple: multiple,
      cashFlows,
      cumulativePV,
      terminalValue,
      terminalPV,
      totalValue,
      sensitivity
    };
  }, [fcf, growthRate, discountRate, terminalMultiple]);

  const formatNumber = (num) => {
    if (num >= 1e9) return `${(num / 1e9).toFixed(1)}B`;
    if (num >= 1e6) return `${(num / 1e6).toFixed(1)}M`;
    if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
    return num.toFixed(2);
  };

  return (
    <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/30">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-white">
          <TrendingUp className="w-5 h-5 text-purple-400" />
          Calculateur DCF Simplifié
        </CardTitle>
        <p className="text-sm text-gray-400">
          Estimez la valeur intrinsèque d'une entreprise en actualisant ses flux de trésorerie futurs (5 ans)
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Free Cash Flow actuel</label>
            <Input
              type="number"
              placeholder="Ex: 100000000"
              value={fcf}
              onChange={(e) => setFcf(e.target.value)}
              className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
            />
            <p className="text-xs text-gray-500 mt-1">En devise (USD, EUR...)</p>
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Croissance annuelle (%)</label>
            <Input
              type="number"
              placeholder="10"
              value={growthRate}
              onChange={(e) => setGrowthRate(e.target.value)}
              className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
            />
            <p className="text-xs text-gray-500 mt-1">Estimation 5 ans</p>
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Taux d'actualisation (%)</label>
            <Input
              type="number"
              placeholder="10"
              value={discountRate}
              onChange={(e) => setDiscountRate(e.target.value)}
              className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
            />
            <p className="text-xs text-gray-500 mt-1">WACC ou taux requis</p>
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Multiple terminal</label>
            <Input
              type="number"
              placeholder="15"
              value={terminalMultiple}
              onChange={(e) => setTerminalMultiple(e.target.value)}
              className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
            />
            <p className="text-xs text-gray-500 mt-1">× FCF année 5</p>
          </div>
        </div>

        {result && (
          <div className="mt-6 space-y-4">
            {/* Main result */}
            <div className="p-5 bg-purple-500/10 rounded-lg border border-purple-500/30 text-center">
              <p className="text-sm text-gray-400 mb-1">Valeur Intrinsèque Estimée</p>
              <p className="text-4xl font-bold text-purple-400">{formatNumber(result.totalValue)}</p>
              <div className="flex justify-center gap-4 mt-3 text-sm">
                <div>
                  <span className="text-gray-400">FCF actualisés : </span>
                  <span className="text-white">{formatNumber(result.cumulativePV)}</span>
                </div>
                <div>
                  <span className="text-gray-400">Valeur terminale : </span>
                  <span className="text-white">{formatNumber(result.terminalPV)}</span>
                </div>
              </div>
            </div>

            {/* Cash flow table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-2 px-3 text-gray-400">Année</th>
                    <th className="text-right py-2 px-3 text-gray-400">FCF Projeté</th>
                    <th className="text-right py-2 px-3 text-gray-400">Facteur</th>
                    <th className="text-right py-2 px-3 text-gray-400">Valeur Actuelle</th>
                  </tr>
                </thead>
                <tbody>
                  {result.cashFlows.map((cf) => (
                    <tr key={cf.year} className="border-b border-white/5">
                      <td className="py-2 px-3 text-white">{cf.year}</td>
                      <td className="py-2 px-3 text-right text-gray-300">{formatNumber(cf.projectedFCF)}</td>
                      <td className="py-2 px-3 text-right text-gray-400">{cf.discountFactor.toFixed(3)}</td>
                      <td className="py-2 px-3 text-right text-emerald-400">{formatNumber(cf.presentValue)}</td>
                    </tr>
                  ))}
                  <tr className="bg-white/5">
                    <td colSpan={3} className="py-2 px-3 text-gray-400">Valeur Terminale (actualisée)</td>
                    <td className="py-2 px-3 text-right text-emerald-400 font-medium">{formatNumber(result.terminalPV)}</td>
                  </tr>
                  <tr className="bg-purple-500/10">
                    <td colSpan={3} className="py-2 px-3 text-white font-medium">Total</td>
                    <td className="py-2 px-3 text-right text-purple-400 font-bold">{formatNumber(result.totalValue)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Sensitivity analysis toggle */}
            <button
              onClick={() => setShowSensitivity(!showSensitivity)}
              className="flex items-center gap-2 text-sm text-purple-400 hover:text-purple-300 transition-colors"
            >
              {showSensitivity ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              Analyse de sensibilité (croissance ±3%)
            </button>

            {showSensitivity && (
              <div className="p-4 bg-gray-900/50 rounded-lg border border-white/10">
                <p className="text-sm text-gray-400 mb-3">Impact de la variation du taux de croissance :</p>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-3 bg-red-500/10 rounded-lg border border-red-500/30">
                    <p className="text-xs text-gray-400 mb-1">Pessimiste ({(result.growthRate - 3).toFixed(0)}%)</p>
                    <p className="text-xl font-bold text-red-400">{formatNumber(result.sensitivity.low)}</p>
                    <p className="text-xs text-red-400">
                      {((result.sensitivity.low / result.sensitivity.base - 1) * 100).toFixed(0)}%
                    </p>
                  </div>
                  <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
                    <p className="text-xs text-gray-400 mb-1">Base ({result.growthRate.toFixed(0)}%)</p>
                    <p className="text-xl font-bold text-purple-400">{formatNumber(result.sensitivity.base)}</p>
                    <p className="text-xs text-purple-400">—</p>
                  </div>
                  <div className="p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                    <p className="text-xs text-gray-400 mb-1">Optimiste ({(result.growthRate + 3).toFixed(0)}%)</p>
                    <p className="text-xl font-bold text-emerald-400">{formatNumber(result.sensitivity.high)}</p>
                    <p className="text-xs text-emerald-400">
                      +{((result.sensitivity.high / result.sensitivity.base - 1) * 100).toFixed(0)}%
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Warning */}
            <div className="flex items-start gap-3 p-4 bg-amber-500/10 rounded-lg border border-amber-500/30">
              <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="text-amber-400 font-medium mb-1">Attention : Le DCF est très sensible aux hypothèses</p>
                <p className="text-gray-400">
                  Ce calculateur est une simplification pédagogique. Une variation de quelques % sur le taux de croissance
                  ou d'actualisation peut changer radicalement la valeur estimée. Utilisez-le comme un outil de réflexion,
                  pas comme une vérité absolue.
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SimpleDCFCalculator;
